/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  Space,
  Button,
  Popover,
  Tag,
  Row,
  Col,
  Skeleton,
  message,
  Tooltip,
  notification,
  Typography
} from 'antd';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { updateNavigationTourGuide, updatePlanModal } from 'store/user';
import { PlusOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import FileSaver from 'file-saver';
import SummaryCard from 'components/SummaryCard';
import EbzTable from 'components/EbzTable';
import EbzButton from 'components/EbzButton';
import EbzFilterGroup from 'components/EbzFilterGroup';
import Alert from 'components/Alert';
import { ExportIcon, NotitficationIcon } from 'utils/icons';
import referralsAPI from 'services/referrals';
import merchantAPI from 'services/merchants';
import currencyFormat from 'utils/currencyFormat';
import { activationStatus, kycStatus, merchantStatus } from 'constants';
import { KYC_STATUS, MERCHANT_STATUS } from 'constants/status';
import dayjs from 'dayjs';
import joyrideCustomTooltip from 'components/JoyrideComponent/JoyrideTooltip';
import joyrideWelcomeCustomTooltip from 'components/JoyrideComponent/JoyrideWelcomeCustomTooltip';
import trackingService from 'services/trackingService';
import EVENT_NAME from 'utils/const';
import { PRODUCT_APP_TYPE, TOUR_REFERAL_CONTENT } from './const';
import ReferralSignUpModal from './ReferralSignUpModal';

const filtersArr = [
  {
    label: 'Product',
    attr: 'products',
    data: PRODUCT_APP_TYPE
  },
  {
    label: 'KYC Status',
    attr: 'kyc_status',
    data: kycStatus
  },
  {
    label: 'Merchant Status',
    attr: 'merchant_status',
    data: merchantStatus
  }
];

const searchArr = [
  {
    value: 'name',
    label: 'Name'
  },
  {
    value: 'merchant_id',
    label: 'Merchant ID'
  },
  {
    value: 'email',
    label: 'Email'
  },
  {
    value: 'mobile_number',
    label: 'Phone'
  }
];

const EbzPopover = styled(Popover)`
  //popover
  .ant-popover-arrow {
    display: none !important;
  }

  .ant-popover-content {
    position: relative;
    top: -10px;
  }
`;

function ReferralsListPage() {
  // Page State
  const [hasSummaryData, setHasSummaryData] = useState(false);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [data, setData] = useState({});
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: false,
      total: 0
    }
  });
  const [filtersObj, setFiltersObj] = useState({});
  //
  const [api, contextHolder] = notification.useNotification();

  const isLeadAllowed = useSelector((state) =>
    state.user.planPermissions.can_view_leads ? state.user.planPermissions.can_view_leads : false
  );
  const isSignUpAllowed = useSelector((state) => state.user.planPermissions.can_view_sign_up);
  const planModal = useSelector((state) => state.user.planModal);
  const dispatch = useDispatch();
  const togglePlanModal = () => {
    dispatch(updatePlanModal(!planModal));
  };

  const navigate = useNavigate();
  const configs = useSelector((state) => state.user.currentUser.configuration);
  const [tourGuideRun, setTourGuideRun] = useState(false);
  const [tourGuideSteps, setTourGuideSteps] = useState([]);
  const isDashboardAllowed = useSelector((state) =>
    state.user.planPermissions.can_view_dashboard
      ? state.user.planPermissions.can_view_dashboard
      : false
  );
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const tourGuide = useSelector((state) => state.user.navigationTourGuidetails);
  const [signUpModelDisplay, setSignUpModelDisplay] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [submitButtonDisable, setSubmitButtonDisable] = useState(false);

  // Apis function
  const fetchReferralsSummary = async (payload) => {
    const response = await referralsAPI.summary(payload);

    if (!response.success) {
      setHasSummaryData(false);
      console.log('something went wrong');
      // Alert('something went wrong', 'Please contact support team for this', 'error');
    }

    setSummary(response?.data ?? []);
  };

  const fetchReferralsList = async (pagination, filters) => {
    setLoading(true);
    setSignUpModelDisplay(false);
    setSignupLoading(false);
    const response = await referralsAPI.list({
      current_page: pagination.current,
      total_counts: pagination.total,
      ...filters
    });

    if (!response.success) {
      console.log('something went wrong');
      // Alert('something went wrong', 'Please contact support team for this', 'error');
      setHasData(false);
    }

    setData(response?.data ?? []);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...pagination,
        total: response?.data.total_counts
      }
    });
  };

  const kycVerify = async (payload) => {
    message.info({
      content: 'Fetching Kyc verification link'
    });

    const response = await merchantAPI.kycVerify({
      merchant_id: payload
    });

    if (response.success) {
      window.open(response.data.redirection_url, '_blank');
      message.info({
        content: 'You are being redirected to link'
      });
    } else {
      console.log('something went wrong');
      Alert('something went wrong', 'Please contact support team for this', 'error');
    }
  };

  const merchantDownloadableReport = async () => {
    if (data.merchants.length > 0) {
      const response = await merchantAPI.downloadableReport({
        current_page: tableParams.pagination.current,
        ...filtersObj
      });

      if (response) {
        api.open({
          message: 'CSV Export',
          description: 'Report has been generated.',
          icon: <NotitficationIcon />
        });
      }

      FileSaver.saveAs(response.data, `referrals_export.csv`);
    } else {
      message.error({
        content: 'No records for CSV export'
      });
    }
  };
  //

  // functions
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter
    });
    fetchReferralsList(pagination, filtersObj);
  };

  const applyFiltersCallbackfunc = (a) => {
    setLoading(true);

    const updatePagination = { ...tableParams.pagination };
    updatePagination.current = 1;
    updatePagination.total = 0;

    const [searchParams, checkboxGroup] = a;
    const filters = {
      ...searchParams,
      ...checkboxGroup
    };
    setFiltersObj(filters);
    fetchReferralsList(updatePagination, filters);
  };

  const clearFiltersCallBackfun = () => {
    setLoading(true);

    const updatePagination = { ...tableParams.pagination };
    updatePagination.current = 1;
    updatePagination.total = 0;

    setFiltersObj({});
    fetchReferralsList(updatePagination);
  };
  //

  const columns = [
    {
      title: <Space>Name</Space>,
      dataIndex: 'name',
      key: 'name',
      ellipsis: {
        showTitle: false
      }
    },
    {
      title: 'Merchant ID',
      dataIndex: 'merchant_id',
      key: 'merchant_id',
      width: '150px'
    },
    {
      title: 'Phone Number',
      dataIndex: 'mobile_number',
      key: 'mobile_number',
      width: '150px'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: {
        showTitle: false
      }
    },
    {
      title: 'Date Created',
      dataIndex: 'merchant_created_time',
      key: 'merchant_created_time',
      align: 'center',
      width: '140px',
      render: (e) => {
        return e === null ? 'Invalid Date' : dayjs(e).format("DD MMM, 'YY");
      }
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: '180px',
      render: (e) => {
        return e === null ? '---' : e;
      }
    },
    // {
    //   title: 'GTV',
    //   dataIndex: 'gtv',
    //   key: 'gtv',
    //   width: '180px',
    //   render: (e) => {
    //     return currencyFormat(e, true, true);
    //   }
    // },
    // {
    //   title: 'Profits',
    //   dataIndex: 'profit',
    //   key: 'profit',
    //   width: '180px',
    //   render: (e) => {
    //     return currencyFormat(e, true, true);
    //   }
    // },
    // {
    //   title: <Space>Activation Status</Space>,
    //   dataIndex: 'status',
    //   key: 'status',
    //   align: 'center',
    //   render: (e) => {
    //     return ACTIVATION_STATUS[e] ? (
    //       <Tag
    //         color={ACTIVATION_STATUS[e].bgColor}
    //         style={{ padding: '2px 13px', color: ACTIVATION_STATUS[e].color, fontSize: '12px' }}
    //       >
    //         {ACTIVATION_STATUS[e].label}
    //       </Tag>
    //     ) : (
    //       <Tag color="#EBEBEB" style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}>
    //         {e}
    //       </Tag>
    //     );
    //   }
    // },
    {
      title: (
        <Space>
          Merchant Status
          <Popover
            placement="bottomRight"
            content={
              <Row>
                <Col span={24}>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.LIVE_MERCHANT.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.LIVE_MERCHANT.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals who are live and transacting.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.LIVE_NOT_ACTIVE.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.LIVE_NOT_ACTIVE.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals for whom KYC has been approved but not started transacting.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.INACTIVE_MERCHANTS.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.INACTIVE_MERCHANTS.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals who have gone live but not transacted in the past 30 days.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.PENDING_MERCHANTS.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.PENDING_MERCHANTS.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals who have signed-up but not yet live.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            }
            trigger="hover"
            overlayClassName="ebz-filters-popover"
            overlayStyle={{
              width: '300px'
            }}
          >
            <InfoCircleOutlined style={{ color: '#A0A0A0' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'merchant_status',
      key: 'merchant_status',
      align: 'center',
      width: '150px',
      render: (e, r) => {
        return MERCHANT_STATUS[e] ? (
          <Tag
            color={MERCHANT_STATUS[e].bgColor}
            style={{ padding: '2px 13px', color: MERCHANT_STATUS[e].color, fontSize: '12px' }}
          >
            {MERCHANT_STATUS[e].label}
          </Tag>
        ) : (
          <>
            <Tag
              color="#EBEBEB"
              style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}
            >
              {e}
            </Tag>
            {/* {c} */}
          </>
        );
      }
    },
    {
      title: (
        <Space>
          KYC Status
          <Popover
            placement="bottomRight"
            content={
              <Row>
                <Col span={24}>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.NOT_STARTED.bgColor
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.NOT_STARTED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            The affiliate is <b>yet to start</b> filling the KYC details.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.NOT_UPLOADED.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.NOT_UPLOADED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            The affiliate has started filling the KYC information, but is
                            <b> yet to complete</b> the process.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.PENDING_APPROVAL.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.PENDING_APPROVAL.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            The affiliate has submitted the KYC information and the application is
                            <b> under review</b> by Easebuzz.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.REJECTED.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.REJECTED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            The affiliate’s KYC has been <b>rejected</b>. They may re-submit their
                            KYC information.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.APPROVED.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col span={22}>
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.APPROVED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            The affiliate’s KYC has been <b>approved</b>.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            }
            trigger="hover"
            overlayClassName="ebz-filters-popover"
            overlayStyle={{
              width: '300px'
            }}
          >
            <InfoCircleOutlined style={{ color: '#A0A0A0' }} />
          </Popover>
        </Space>
      ),
      width: '150px',
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      align: 'center',
      render: (e, r) => {
        const showicon = (
          <Tooltip placement="top" title="Kyc Verify">
            <InfoCircleOutlined
              style={{ color: '#A0A0A0', cursor: 'pointer' }}
              onClick={(ev) => {
                ev.stopPropagation();
                kycVerify(r.merchant_id);
              }}
            />
          </Tooltip>
        );
        return KYC_STATUS[e] ? (
          <Tag
            color={KYC_STATUS[e].bgColor}
            style={{ padding: '2px 13px', color: KYC_STATUS[e].color, fontSize: '12px' }}
          >
            {KYC_STATUS[e].label}
          </Tag>
        ) : (
          <>
            <Tag
              color="#EBEBEB"
              style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}
            >
              {e}
            </Tag>
            {/* {c} */}
          </>
        );
      }
    }
  ];

  const handleSignUpModal = () => {
    setSignUpModelDisplay(!signUpModelDisplay);
  };

  const fetchAndUpdateNavigation = async (paramKey) => {
    const request = {
      update: paramKey
    };
    const response = await merchantAPI.fetchNavigationFlag(request);
    if (response.success) {
      dispatch(updateNavigationTourGuide(response?.data));
    } else {
      console.log('something went wrong');
    }
  };

  const handleJoyrideCallback = (callbackProps) => {
    const { status, index, type, action, size } = callbackProps;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if ([ACTIONS.NEXT].includes(action)) {
        const nextIndex = index + 1;
        setTourStepIndex(nextIndex);
      } else {
        const prevIndex = index - 1;
        setTourStepIndex(prevIndex);
      }
      if (isDashboardAllowed && index === 1 && [ACTIONS.PREV].includes(action)) {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_refferal_guide');
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setTourGuideRun(false);
    }
    const lastStepIndex = size - index;
    if ([ACTIONS.CLOSE].includes(action) || [EVENTS.TOUR_END].includes(type)) {
      if (lastStepIndex > 1) {
        setTourStepIndex(size - 1);
      } else {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_refferal_guide');
      }
    }
  };

  // effects
  useEffect(() => {
    fetchReferralsSummary();
    fetchReferralsList(tableParams.pagination);
    // Alert('something went wrong', 'Please contact support team for this', 'error');
  }, []);

  useEffect(() => {
    if (Object.keys(data).length !== 0) {
      setHasData(true);
    }
  }, [data]);

  useEffect(() => {
    if (Object.keys(summary).length !== 0) {
      setHasSummaryData(true);
    }
  }, [summary]);
  //
  useEffect(() => {
    if (
      tourGuide?.dashboard_guide?.tour_guide &&
      tourGuide?.dashboard_guide?.dashboard_modules?.disable_refferal_guide
    ) {
      setTourGuideRun(!tourGuideRun);
      setTourGuideSteps(TOUR_REFERAL_CONTENT);
      setTourStepIndex(isDashboardAllowed ? 1 : 0);
    }
  }, [tourGuide, isDashboardAllowed]);

  return (
    <>
      <div
        style={{
          padding: '0 20px',
          margin: '20px 0px'
        }}
      >
        <Joyride
          callback={handleJoyrideCallback}
          continuous
          run={tourGuideRun}
          steps={tourGuideSteps}
          scrollOffset={64}
          showSkipButton
          styles={{
            options: {
              primaryColor: 'var(--color-fill-alter)',
              zIndex: 100
            }
          }}
          tooltipComponent={isDashboardAllowed ? joyrideCustomTooltip : joyrideWelcomeCustomTooltip}
          stepIndex={tourStepIndex}
        />
        {hasSummaryData === true ? (
          <SummaryCard summary={summary?.summary} updatedAt={summary?.updated_at} />
        ) : (
          <Row
            gutter={[8, 16]}
            style={{
              boxShadow: 'rgb(0 0 0 / 6%) 0px 3px 6px 4px',
              borderRadius: '10px',
              backgroundColor: 'white'
            }}
          >
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%',
                border: '4px solid',
                borderTop: '0',
                borderBottom: '0',
                borderLeft: '0',
                borderImage:
                  'linear-gradient(rgb(255, 255, 255) 10%, rgb(232, 232, 232) 48%, rgb(255, 255, 255) 90%) 1 / 1 / 0 stretch',
                borderImageSlice: '1'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%',
                border: '4px solid',
                borderTop: '0',
                borderBottom: '0',
                borderLeft: '0',
                borderImage:
                  'linear-gradient(rgb(255, 255, 255) 10%, rgb(232, 232, 232) 48%, rgb(255, 255, 255) 90%) 1 / 1 / 0 stretch',
                borderImageSlice: '1'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%',
                border: '4px solid',
                borderTop: '0',
                borderBottom: '0',
                borderLeft: '0',
                borderImage:
                  'linear-gradient(rgb(255, 255, 255) 10%, rgb(232, 232, 232) 48%, rgb(255, 255, 255) 90%) 1 / 1 / 0 stretch',
                borderImageSlice: '1'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
          </Row>
        )}
      </div>
      <div
        style={{
          padding: '10px 24px'
        }}
      >
        <Space
          direction="horizontal"
          style={{ width: '100%', paddingBottom: '10px', justifyContent: 'space-between' }}
        >
          <Space>
            <EbzPopover
              placement="bottomLeft"
              content={
                <EbzFilterGroup
                  searchParams={searchArr}
                  filters={filtersArr}
                  apply={applyFiltersCallbackfunc}
                  clear={clearFiltersCallBackfun}
                />
              }
              trigger="click"
              overlayStyle={{
                width: '400px'
              }}
              overlayClassName="ebz-filters-popover"
            >
              <EbzButton size="large" className="ebz-filters-btn">
                Search & Filter <DownOutlined />
              </EbzButton>
            </EbzPopover>
          </Space>
          <Space size={10}>
            {isSignUpAllowed ? (
              <Button
                icon={<PlusOutlined />}
                type="primary"
                size="large"
                style={{ boxShadow: '0px 3px 6px #0000000f' }}
                className="sign-up"
                onClick={() => {
                  handleSignUpModal();
                  trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, {
                    name: 'Sign Up'
                  });
                }}
              >
                Sign Up
              </Button>
            ) : (
              ''
            )}

            {loading ? (
              <Button disabled icon={<ExportIcon />} size="large" className="export-btn">
                Export
              </Button>
            ) : (
              <EbzButton
                icon={<ExportIcon />}
                size="large"
                className="export-btn"
                onClick={merchantDownloadableReport}
              >
                Export
              </EbzButton>
            )}
            {isLeadAllowed === true ? (
              <Link to="/leads/create">
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  size="large"
                  onClick={() =>
                    trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, {
                      name: 'Create Lead'
                    })
                  }
                  style={{ boxShadow: '0px 3px 6px #0000000f' }}
                  className="create-lead"
                >
                  Create Lead
                </Button>
              </Link>
            ) : (
              isLeadAllowed === false && (
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  size="large"
                  style={{ boxShadow: '0px 3px 6px #0000000f' }}
                  disabled
                  className="create-lead"
                >
                  Create Lead
                </Button>
              )
            )}
          </Space>
        </Space>
        <EbzTable
          columns={columns}
          rowKey={(r) => `${r.merchant_id}_referral`}
          dataSource={hasData ? data.merchants : []}
          onRow={(r) => {
            return {
              onClick: () => navigate(`details/${r.merchant_id}`)
            };
          }}
          loading={loading}
          pagination={tableParams.pagination}
          onChange={handleTableChange}
          size="middle"
        />
        <ReferralSignUpModal
          signUpModelDisplay={signUpModelDisplay}
          toggleModal={handleSignUpModal}
          signupLoading={signupLoading}
          submitButtonDisable={submitButtonDisable}
          setSubmitButtonDisable={setSubmitButtonDisable}
          setSignupLoading={setSignupLoading}
          fetchReferralsList={fetchReferralsList}
          pagination={tableParams.pagination}
        />
      </div>
      {contextHolder}
    </>
  );
}

export default ReferralsListPage;
